import {
  Drawer,
  DrawerProps,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  styled,
  useTheme,
} from '@mui/material';
import { SvgIconComponent } from '@mui/icons-material';
import AccountIcon from '@mui/icons-material/AccountCircle';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import MenuIcon from '@mui/icons-material/Menu';
import SecurityIcon from '@mui/icons-material/Security';
import React from 'react';
import { RouteProps, useNavigate, useLocation } from 'react-router';

export const DRAWER_WIDTH = 240;
export type AdminDrawerValues = 'Users' | 'Roles';

export interface AdminDrawerProps {
  open: boolean;
  onToggle: () => void;
}

const drawerValuesRoutesMap: Record<AdminDrawerValues, RouteProps> = {
  Users: { path: '/users' },
  Roles: { path: '/roles' },
};

const prefix = 'drawer';
const classes = {
  drawerPaper: `${prefix}-paper`,
  drawerContainer: `${prefix}-container`,
  itemIcon: `${prefix}-itemicon`,
  activeItem: `${prefix}-active-item`,
};

const StyledDrawer = styled((props: DrawerProps) => <Drawer {...props} />)(({ theme }) => ({
  [`& .${classes.drawerPaper}`]: {
    backgroundColor: theme.palette.primary.dark,
    color: theme.palette.getContrastText(theme.palette.primary.dark),
    width: DRAWER_WIDTH,
    top: 48,
    height: 'calc(100% - 48px)',
    overflowX: 'hidden',
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.standard,
    }),
  },
  [`& .${classes.drawerContainer}`]: {
    overflow: 'auto',
  },
  [`& .${classes.itemIcon}`]: {
    color: theme.palette.getContrastText(theme.palette.primary.dark),
  },
  [`& .${classes.activeItem}`]: {
    backgroundColor: `${theme.palette.primary.light} !important`,
  },
}));

export function AdminDrawer({ open, onToggle }: AdminDrawerProps): JSX.Element {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  const activeItem = React.useMemo<AdminDrawerValues>(() => {
    const matched = Object.entries(drawerValuesRoutesMap).find(
      ([, v]) => location.pathname === `/admin${v.path}`,
    );
    return matched ? (matched[0] as AdminDrawerValues) : 'Users';
  }, [location.pathname]);

  const DrawerItem = React.useCallback(
    ({ Icon, text, route }: { Icon: SvgIconComponent; text: AdminDrawerValues; route: string }) => (
      <ListItem
        button
        className={activeItem === text ? classes.activeItem : undefined}
        onClick={() => navigate(route)}
      >
        <ListItemIcon>
          <Icon className={classes.itemIcon} />
        </ListItemIcon>
        <ListItemText>{text}</ListItemText>
      </ListItem>
    ),
    [activeItem, navigate],
  );

  return (
    <>
      {/* Toggle button — slides along the drawer edge */}
      <Tooltip title={open ? 'Collapse menu' : 'Expand menu'} placement="right">
        <IconButton
          onClick={onToggle}
          size="small"
          aria-label="toggle menu"
          sx={{
            position: 'fixed',
            top: 56,
            left: open ? DRAWER_WIDTH - 16 : 8,
            zIndex: theme.zIndex.drawer + 1,
            transition: theme.transitions.create('left', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.standard,
            }),
            backgroundColor: theme.palette.primary.dark,
            color: theme.palette.getContrastText(theme.palette.primary.dark),
            width: 28,
            height: 28,
            boxShadow: theme.shadows[3],
            '&:hover': {
              backgroundColor: theme.palette.primary.main,
            },
          }}
        >
          {open ? <ChevronLeftIcon sx={{ fontSize: 18 }} /> : <MenuIcon sx={{ fontSize: 18 }} />}
        </IconButton>
      </Tooltip>

      <StyledDrawer
        variant="permanent"
        classes={{ paper: classes.drawerPaper }}
        sx={{
          '& .MuiDrawer-paper': {
            width: open ? DRAWER_WIDTH : 0,
          },
        }}
      >
        <div className={classes.drawerContainer}>
          <List sx={{ pt: 4 }}>
            <DrawerItem text="Users" route={'users'} Icon={AccountIcon} />
            <DrawerItem text="Roles" route={'roles'} Icon={SecurityIcon} />
          </List>
        </div>
      </StyledDrawer>
    </>
  );
}
